import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { SoccerBall, Cards, ShieldCheck, Star, X, FirstAid } from '@phosphor-icons/react';

/**
 * Shared modal to view highlights + stats of any played match (league/cup/ac/cwc)
 * Props: isOpen, onClose, match { homeTeamName, awayTeamName, homeScore, awayScore, events, matchRatings }
 */
export const MatchHighlightsModal = ({ isOpen, onClose, match, homeTeam, awayTeam, competitionLabel = 'Match' }) => {
  if (!match) return null;
  const events = match.events || [];
  const goals = events.filter(e => e.type === 'goal');
  const cards = events.filter(e => e.type === 'yellow_card' || e.type === 'red_card');
  const saves = events.filter(e => e.type === 'save');
  const injuries = events.filter(e => e.type === 'injury');
  const ratings = match.matchRatings || {};

  const homeName = match.homeTeamName || homeTeam?.name;
  const awayName = match.awayTeamName || awayTeam?.name;

  // Match stats
  const homeGoals = events.filter(e => e.type === 'goal' && e.teamName === homeName).length;
  const awayGoals = events.filter(e => e.type === 'goal' && e.teamName === awayName).length;
  const homeShots = events.filter(e => (e.type === 'goal' || e.type === 'save') && e.teamName === homeName).length;
  const awayShots = events.filter(e => (e.type === 'goal' || e.type === 'save') && e.teamName === awayName).length;
  const homeCards = events.filter(e => (e.type === 'yellow_card' || e.type === 'red_card') && e.teamName === homeName).length;
  const awayCards = events.filter(e => (e.type === 'yellow_card' || e.type === 'red_card') && e.teamName === awayName).length;

  // Top rated players
  const topRated = Object.entries(ratings)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([pid, rating]) => {
      let player = null, team = null;
      [homeTeam, awayTeam].forEach(t => {
        if (!t) return;
        const p = t.players?.find(pl => pl.id === pid);
        if (p) { player = p; team = t; }
      });
      return { player, team, rating };
    })
    .filter(x => x.player);

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#121216] border-[#2C2C35] max-w-3xl p-0" data-testid="match-highlights-modal">
        <DialogHeader className="px-5 py-3 border-b border-[#2C2C35] flex flex-row items-center justify-between">
          <DialogTitle className="font-heading text-white uppercase text-base tracking-tight">{competitionLabel} Highlights</DialogTitle>
          <button onClick={onClose} className="text-[#A0A0A5] hover:text-white" data-testid="close-highlights-btn"><X size={18} /></button>
        </DialogHeader>

        {/* Scoreline */}
        <div className="bg-gradient-to-b from-[#1A1A1F] to-[#0A0A0C] px-6 py-5">
          <div className="flex items-center justify-center gap-6">
            <div className="flex-1 text-right">
              <p className="font-heading font-bold text-white text-lg truncate">{homeName}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="score-display text-4xl text-white">{match.homeScore ?? homeGoals}</span>
              <span className="text-2xl text-[#6E6E73]">-</span>
              <span className="score-display text-4xl text-white">{match.awayScore ?? awayGoals}</span>
            </div>
            <div className="flex-1 text-left">
              <p className="font-heading font-bold text-white text-lg truncate">{awayName}</p>
            </div>
          </div>
        </div>

        <ScrollArea className="max-h-[70vh]">
          <div className="p-5 space-y-5">

            {/* Match stats bar */}
            <div className="card-elevated rounded-sm p-4">
              <h4 className="font-heading font-bold text-white uppercase text-sm mb-3">Match Stats</h4>
              <MatchStatBar label="Goals" h={homeGoals} a={awayGoals} />
              <MatchStatBar label="Shots" h={homeShots} a={awayShots} />
              <MatchStatBar label="Cards" h={homeCards} a={awayCards} />
            </div>

            {/* Goals timeline */}
            {goals.length > 0 && (
              <div className="card-elevated rounded-sm p-4">
                <h4 className="font-heading font-bold text-white uppercase text-sm mb-3 flex items-center gap-2">
                  <SoccerBall size={16} weight="fill" className="text-[#32D74B]" /> Goals
                </h4>
                <div className="space-y-1.5">
                  {goals.sort((a, b) => a.minute - b.minute).map((g, i) => (
                    <div key={i} className={`flex items-center gap-3 text-sm ${g.teamName === homeName ? '' : 'flex-row-reverse text-right'}`}>
                      <span className="font-mono text-xs text-[#6E6E73] w-8">{g.minute}'</span>
                      <span className="flex-1 text-white">
                        <SoccerBall size={14} weight="fill" className="inline text-[#32D74B] mr-1" />
                        {g.player?.flag || ''} <span className="font-medium">{g.player?.name || 'Unknown'}</span>
                        {g.assister && <span className="text-[10px] text-[#A0A0A5] ml-1">(assist: {g.assister.name})</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cards */}
            {cards.length > 0 && (
              <div className="card-elevated rounded-sm p-4">
                <h4 className="font-heading font-bold text-white uppercase text-sm mb-3 flex items-center gap-2">
                  <Cards size={16} weight="fill" className="text-[#FFD60A]" /> Cards
                </h4>
                <div className="space-y-1">
                  {cards.map((c, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <span className={`w-2.5 h-3.5 rounded-sm ${c.type === 'red_card' ? 'bg-[#FF3B30]' : 'bg-[#FFD60A]'}`} />
                      <span className="font-mono text-xs text-[#6E6E73] w-8">{c.minute}'</span>
                      <span className="text-white flex-1">{c.player?.name} <span className="text-[10px] text-[#A0A0A5]">({c.teamName})</span></span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Saves */}
            {saves.length > 0 && (
              <div className="card-elevated rounded-sm p-4">
                <h4 className="font-heading font-bold text-white uppercase text-sm mb-3 flex items-center gap-2">
                  <ShieldCheck size={16} weight="fill" className="text-[#007AFF]" /> Notable Saves
                </h4>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  {saves.slice(0, 8).map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-[#A0A0A5]">
                      <span className="font-mono w-8">{s.minute}'</span>
                      <span className="text-white truncate">{s.player?.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Injuries */}
            {injuries.length > 0 && (
              <div className="card-elevated rounded-sm p-4">
                <h4 className="font-heading font-bold text-white uppercase text-sm mb-3 flex items-center gap-2">
                  <FirstAid size={16} weight="fill" className="text-[#FF6B00]" /> Injuries
                </h4>
                <div className="space-y-1 text-sm">
                  {injuries.map((inj, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="font-mono text-xs text-[#6E6E73] w-8">{inj.minute}'</span>
                      <span className="text-white flex-1">{inj.player?.name} ({inj.teamName})</span>
                      <span className="text-[#FF6B00] text-xs">{inj.injuryWeeks}w out</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top rated players */}
            {topRated.length > 0 && (
              <div className="card-elevated rounded-sm p-4">
                <h4 className="font-heading font-bold text-white uppercase text-sm mb-3 flex items-center gap-2">
                  <Star size={16} weight="fill" className="text-[#FFD700]" /> Top Rated Players
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {topRated.map((x, i) => (
                    <div key={i} className="bg-[#0A0A0C] rounded-sm p-2 flex items-center gap-2">
                      <span className={`font-mono font-bold text-lg ${x.rating >= 8.5 ? 'text-[#FFD700]' : x.rating >= 7.5 ? 'text-[#32D74B]' : 'text-[#A0A0A5]'}`}>{x.rating}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white truncate">{x.player.flag} {x.player.name}</p>
                        <p className="text-[9px] text-[#6E6E73]">{x.team?.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Commentary feed */}
            <div className="card-elevated rounded-sm p-4">
              <h4 className="font-heading font-bold text-white uppercase text-sm mb-3">Commentary</h4>
              <div className="space-y-1 text-xs">
                {events.filter(e => e.commentary || e.message).map((e, i) => (
                  <div key={i} className="flex gap-2">
                    {e.minute != null && <span className="font-mono text-[#6E6E73] w-8 flex-shrink-0">{e.minute}'</span>}
                    <span className="text-[#A0A0A5]">{e.commentary || e.message}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

const MatchStatBar = ({ label, h, a }) => {
  const total = h + a;
  const homePct = total === 0 ? 50 : (h / total) * 100;
  return (
    <div className="mb-2">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-mono text-white font-bold">{h}</span>
        <span className="text-[#A0A0A5] uppercase tracking-wide">{label}</span>
        <span className="font-mono text-white font-bold">{a}</span>
      </div>
      <div className="flex h-1.5 bg-[#2C2C35] rounded-sm overflow-hidden">
        <div className="bg-[#FF3B30]" style={{ width: `${homePct}%` }} />
        <div className="bg-[#007AFF] flex-1" />
      </div>
    </div>
  );
};
